import { useEffect, useState } from 'react'

// 시간이 흐르는 글자는 값이 아니라 시계가 흐르게 해야 한다. 목록을 다시 읽는 간격이 8~30초라 초가 그만큼 멈춰 있다가
// 뛰고, 같은 응답은 `usePoll` 이 이전 객체로 두어 화면을 아예 다시 그리지 않으므로(`ui/share.ts`) 폴링에 기댄
// `방금` 은 영영 `방금` 이다. 그래서 다시 읽지 않고 지금 시각만 바꾼다 — 초를 세는 경과는 1초, 분까지인
// 상대 시각(`3분 전`·`12분 뒤`)은 1분마다.
//
// 타이머는 시계마다 하나를 구독자 전체가 나눠 쓴다: 준비 중인 노드 카드가 스물이어도 1초에 한 번이다.
// 탭이 숨으면 알리지 않는다 — 보이지 않는 화면을 다시 그리지 않는다(`usePoll` 과 같다). 탭으로 돌아오면
// 틱을 기다리지 않고 곧바로 알린다 — 몇 시간 뒤에 돌아온 화면이 1분 동안 `방금` 이라고 하지 않게.

type Clock = { ms: number; listeners: Set<() => void>; timer: number | undefined }

const SECOND: Clock = { ms: 1000, listeners: new Set(), timer: undefined }
const MINUTE: Clock = { ms: 60_000, listeners: new Set(), timer: undefined }

function notify(c: Clock) {
  if (document.hidden) return
  for (const n of c.listeners) n()
}

const onVisible = () => {
  notify(SECOND)
  notify(MINUTE)
}

function subscribe(c: Clock, listener: () => void): () => void {
  c.listeners.add(listener)
  c.timer ??= window.setInterval(() => notify(c), c.ms)
  if (SECOND.listeners.size + MINUTE.listeners.size === 1) document.addEventListener('visibilitychange', onVisible)
  return () => {
    c.listeners.delete(listener)
    if (c.listeners.size === 0 && c.timer !== undefined) {
      window.clearInterval(c.timer)
      c.timer = undefined
    }
    if (SECOND.listeners.size + MINUTE.listeners.size === 0) document.removeEventListener('visibilitychange', onVisible)
  }
}

function useClock(c: Clock): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => subscribe(c, () => setNow(Date.now())), [c])
  return now
}

/** 1초마다 갱신되는 지금 시각. 초까지 세는 경과 시간을 내는 자리에서만 부른다. */
export function useNow(): number {
  return useClock(SECOND)
}

/** 1분마다 갱신되는 지금 시각. 분까지인 상대 시각은 이것을 읽는 `components/Ago.tsx` 로 낸다. */
export function useMinute(): number {
  return useClock(MINUTE)
}
